/**
 * useSimulatorSessions — list of all simulador sessions for the current user
 * (newest first). Used by the Histórico page.
 *
 * Scopes by `profileId` (Clerk → profiles mapping) + explicit `.eq("user_id")`
 * — same pattern as `useUserProcedures` to avoid RLS-bridge gotchas.
 */
import { useQuery } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useProfileId } from "@/features/onboarding/hooks/useProfileId";
import type { SimulatorDifficulty, SimulatorStatus } from "../types";

export interface SessionHistoryRow {
  id: string;
  eneatype: number;
  difficulty: SimulatorDifficulty;
  status: SimulatorStatus;
  final_score: number | null;
  close_verdict: "accept" | "delay" | "decline" | null;
  procedure_snapshot: { name?: string; value_cents?: number | null } | null;
  persona_snapshot: { name?: string; age?: number; profession?: string } | null;
  created_at: string;
  ended_at: string | null;
  last_seq: number;
}

export function useSimulatorSessions() {
  const { client, isReady } = useSupabaseClient();
  const { profileId } = useProfileId();

  return useQuery<SessionHistoryRow[]>({
    queryKey: ["simulador", "sessions", profileId],
    enabled: Boolean(profileId) && isReady,
    staleTime: 15_000,
    queryFn: async () => {
      if (!profileId) return [];
      const { data, error } = await client
        .from("simulator_sessions")
        .select(
          "id, eneatype, difficulty, status, final_score, close_verdict, procedure_snapshot, persona_snapshot, created_at, ended_at, last_seq",
        )
        .eq("user_id", profileId)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as SessionHistoryRow[];
    },
  });
}
